import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  alpha,
  useTheme,
  CircularProgress,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  SwapHoriz as MovementIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ViewColumn as ColumnsIcon,
  TrendingUp as InIcon,
  TrendingDown as OutIcon,
  Inventory as StockIcon,
  CalendarToday as DateIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ViewAgenda as CompactIcon,
  ViewDay as StandardIcon,
  ViewModule as ComfortableIcon,
  FilterAlt as ActiveFilterIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Analytics as AnalyticsIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import ReportHeader from '../../components/Reports/ReportHeader';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Stock Movements Report',
    titleAr: 'تقرير حركات المخزون',
    filters: 'Filters',
    item: 'Item',
    warehouse: 'Warehouse',
    branch: 'Branch',
    movementType: 'Movement Type',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    allItems: 'All Items',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    allTypes: 'All Movement Types',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    movementDate: 'Date',
    referenceNumber: 'Document #',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    warehouseName: 'Warehouse',
    branchName: 'Branch',
    movementTypeLabel: 'Type',
    quantityIn: 'Quantity In',
    quantityOut: 'Quantity Out',
    balance: 'Balance',
    unitCost: 'Unit Cost',
    totalValue: 'Total Value',
    notes: 'Notes',
    loading: 'Loading...',
    noData: 'No movements found',
    rowsPerPage: 'Rows per page:',
    totalMovements: 'Total Movements',
    totalIn: 'Total In',
    totalOut: 'Total Out',
    netMovement: 'Net Movement',
    kwd: 'KWD',
    typeTotal: 'Type Total',
    grandTotal: 'GRAND TOTAL',
    purchaseInvoice: 'Purchase Invoice',
    purchaseReturn: 'Purchase Return',
    stockIssue: 'Stock Issue',
    stockReceipt: 'Stock Receipt',
    warehouseTransfer: 'Warehouse Transfer',
    branchTransfer: 'Branch Transfer',
    stockCount: 'Stock Count',
    adjustment: 'Adjustment',
    opening: 'Opening Balance',
    search: 'Search...',
    searchPlaceholder: 'Search by item, document, or notes...',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    thisYear: 'This Year',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    custom: 'Custom',
    clearFilters: 'Clear All Filters',
    activeFilters: 'Active Filters',
    averageCost: 'Avg Cost',
    highValue: 'High Value Movement',
    detailedView: 'Detailed Movements',
    summaryByItem: 'Summary by Item',
    summaryByWarehouse: 'Summary by Warehouse',
    summaryByType: 'Summary by Type',
    itemSummary: 'Item Summary',
    warehouseSummary: 'Warehouse Summary',
    typeSummary: 'Type Summary',
    totalMovementsCount: 'Total Movements',
    currentBalance: 'Current Balance',
    avgQuantity: 'Avg Quantity',
    openingBalance: 'Opening Balance',
    runningBalance: 'Running Balance',
    stockAnalysis: 'Stock Analysis',
    turnoverRatio: 'Turnover Ratio',
    daysOnHand: 'Days on Hand',
    reorderLevel: 'Reorder Level',
    lowStock: 'Low Stock Alert',
    avgCostPerUnit: 'Avg Cost/Unit',
  },
  ar: {
    title: 'Stock Movements Report',
    titleAr: 'تقرير حركات المخزون',
    filters: 'الفلاتر',
    item: 'الصنف',
    warehouse: 'المخزن',
    branch: 'الفرع',
    movementType: 'نوع الحركة',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    allItems: 'جميع الأصناف',
    allWarehouses: 'جميع المخازن',
    allBranches: 'جميع الفروع',
    allTypes: 'جميع أنواع الحركات',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    movementDate: 'التاريخ',
    referenceNumber: 'رقم المستند',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    warehouseName: 'المخزن',
    branchName: 'الفرع',
    movementTypeLabel: 'النوع',
    quantityIn: 'كمية وارد',
    quantityOut: 'كمية صادر',
    balance: 'الرصيد',
    unitCost: 'تكلفة الوحدة',
    totalValue: 'القيمة الإجمالية',
    notes: 'ملاحظات',
    loading: 'جاري التحميل...',
    noData: 'لا توجد حركات',
    rowsPerPage: 'عدد الصفوف:',
    totalMovements: 'إجمالي الحركات',
    totalIn: 'إجمالي الوارد',
    totalOut: 'إجمالي الصادر',
    netMovement: 'صافي الحركة',
    kwd: 'د.ك',
    typeTotal: 'إجمالي النوع',
    grandTotal: 'الإجمالي الكلي',
    purchaseInvoice: 'فاتورة شراء',
    purchaseReturn: 'مرتجع شراء',
    stockIssue: 'صرف مخزون',
    stockReceipt: 'استلام مخزون',
    warehouseTransfer: 'نقل بين مخازن',
    branchTransfer: 'نقل للفرع',
    stockCount: 'جرد مخزون',
    adjustment: 'تسوية',
    opening: 'رصيد افتتاحي',
    search: 'بحث...',
    searchPlaceholder: 'ابحث بالصنف، المستند، أو الملاحظات...',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    thisYear: 'هذه السنة',
    last7Days: 'آخر 7 أيام',
    last30Days: 'آخر 30 يوم',
    custom: 'مخصص',
    clearFilters: 'مسح جميع الفلاتر',
    activeFilters: 'الفلاتر النشطة',
    averageCost: 'متوسط التكلفة',
    highValue: 'حركة عالية القيمة',
    detailedView: 'الحركات التفصيلية',
    summaryByItem: 'ملخص حسب الصنف',
    summaryByWarehouse: 'ملخص حسب المخزن',
    summaryByType: 'ملخص حسب النوع',
    itemSummary: 'ملخص الأصناف',
    warehouseSummary: 'ملخص المخازن',
    typeSummary: 'ملخص الأنواع',
    totalMovementsCount: 'عدد الحركات',
    currentBalance: 'الرصيد الحالي',
    avgQuantity: 'متوسط الكمية',
    openingBalance: 'الرصيد الافتتاحي',
    runningBalance: 'الرصيد الجاري',
    stockAnalysis: 'تحليل المخزون',
    turnoverRatio: 'معدل الدوران',
    daysOnHand: 'أيام التخزين',
    reorderLevel: 'نقطة إعادة الطلب',
    lowStock: 'تنبيه مخزون منخفض',
    avgCostPerUnit: 'متوسط التكلفة/وحدة',
  }
};

interface Item {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
}

interface Warehouse {
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  warehouseNameAr: string;
}

interface Branch {
  branchId: number;
  branchCode: string;
  branchName: string;
  branchNameAr: string;
}

interface StockMovement {
  movementId: number;
  movementType: string;
  referenceNumber: string;
  movementDate: string;
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  unitId: number;
  unitCode: string;
  unitName: string;
  unitNameAr: string;
  quantityIn: number;
  quantityOut: number;
  unitCost: number;
  totalValue: number;
  warehouseId?: number;
  warehouseCode?: string;
  warehouseName?: string;
  warehouseNameAr?: string;
  branchId?: number;
  branchCode?: string;
  branchName?: string;
  branchNameAr?: string;
  notes?: string;
  openingBalance?: number; // Opening balance for the item
  runningBalance?: number; // Running balance after this transaction
  isOpeningRow?: boolean; // Flag to identify opening balance row
}

const StockMovementsReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StockMovement[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  const [items, setItems] = useState<Item[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  
  const [itemFilter, setItemFilter] = useState<number | ''>('');
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [movementTypeFilter, setMovementTypeFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [tableDensity, setTableDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');
  const [showFilters, setShowFilters] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const [visibleColumns, setVisibleColumns] = useState({
    movementDate: true,
    referenceNumber: true,
    itemCode: true,
    itemName: true,
    unitName: true,
    warehouseName: true,
    branchName: false,
    movementType: true,
    quantityIn: true,
    quantityOut: true,
    runningBalance: true,
    unitCost: true,
    totalValue: true,
    notes: false,
  });

  const [stats, setStats] = useState({
    totalMovements: 0,
    totalIn: 0,
    totalOut: 0,
    netMovement: 0,
    totalValue: 0,
    averageCost: 0,
  });

  useEffect(() => {
    loadCompanyInfo();
    loadDropdownData();
  }, []);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, itemFilter, warehouseFilter, branchFilter, movementTypeFilter, dateFromFilter, dateToFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [itemsResponse, warehousesResponse, branchesResponse] = await Promise.all([
        apiClient.get<Item[]>('/Items'),
        apiClient.get<Warehouse[]>('/Warehouses'),
        apiClient.get<Branch[]>('/Branches'),
      ]);
      
      setItems(itemsResponse.data);
      setWarehouses(warehousesResponse.data);
      setBranches(branchesResponse.data);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const params: any = {};
      if (itemFilter) params.itemId = itemFilter;
      if (warehouseFilter) params.warehouseId = warehouseFilter;
      if (branchFilter) params.branchId = branchFilter;
      if (movementTypeFilter) params.movementType = movementTypeFilter;
      if (dateFromFilter) params.fromDate = dateFromFilter;
      if (dateToFilter) params.toDate = dateToFilter;

      const response = await apiClient.get<StockMovement[]>('/StockMovements', { params });
      let movements = response.data;

      // Group movements by Item + Warehouse + Unit
      const grouped = new Map<string, StockMovement[]>();
      movements.forEach(mov => {
        const key = `${mov.itemId}_${mov.warehouseId || 0}_${mov.unitId}`;
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key)!.push(mov);
      });

      // Add Opening Balance rows and calculate Running Balance
      const enhancedData: StockMovement[] = [];
      
      grouped.forEach((movs, key) => {
        // Sort by date
        movs.sort((a, b) => new Date(a.movementDate).getTime() - new Date(b.movementDate).getTime());
        
        // Calculate opening balance (sum of all IN - OUT before first movement)
        let openingBalance = 0;
        
        // Add Opening Balance row
        if (movs.length > 0) {
          const firstMov = movs[0];
          enhancedData.push({
            ...firstMov,
            movementId: -1, // Special ID for opening balance
            movementType: 'Opening Balance',
            referenceNumber: '--',
            quantityIn: 0,
            quantityOut: 0,
            unitCost: 0,
            totalValue: 0,
            openingBalance: openingBalance,
            runningBalance: openingBalance,
            isOpeningRow: true,
            notes: 'Opening Balance'
          });
        }
        
        // Add movements with running balance
        let runningBalance = openingBalance;
        movs.forEach((mov, index) => {
          runningBalance += mov.quantityIn - mov.quantityOut;
          enhancedData.push({
            ...mov,
            openingBalance: openingBalance,
            runningBalance: runningBalance,
            isOpeningRow: false
          });
        });
      });

      // Sort final data by item, warehouse, then date
      enhancedData.sort((a, b) => {
        if (a.itemId !== b.itemId) return a.itemId - b.itemId;
        if ((a.warehouseId || 0) !== (b.warehouseId || 0)) return (a.warehouseId || 0) - (b.warehouseId || 0);
        return new Date(a.movementDate).getTime() - new Date(b.movementDate).getTime();
      });

      setData(enhancedData);
      setTotalRecords(enhancedData.length);

      // Calculate stats (excluding opening balance rows)
      const actualMovements = enhancedData.filter(m => !m.isOpeningRow);
      const calculatedStats = actualMovements.reduce((acc, mov) => ({
        totalMovements: acc.totalMovements + 1,
        totalIn: acc.totalIn + mov.quantityIn,
        totalOut: acc.totalOut + mov.quantityOut,
        netMovement: acc.netMovement + (mov.quantityIn - mov.quantityOut),
        totalValue: acc.totalValue + mov.totalValue,
        totalCost: acc.totalCost + mov.totalValue,
      }), { totalMovements: 0, totalIn: 0, totalOut: 0, netMovement: 0, totalValue: 0, totalCost: 0 });

      const avgCost = calculatedStats.totalMovements > 0 
        ? calculatedStats.totalCost / calculatedStats.totalMovements 
        : 0;

      setStats({...calculatedStats, averageCost: avgCost});
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleColumnToggle = (column: keyof typeof visibleColumns) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const formatNumber = (num: number | undefined, decimals: number = 3): string => {
    if (num === undefined || num === null) return '0.000';
    return num.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const getMovementTypeLabel = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'PurchaseInvoice': t.purchaseInvoice,
      'PurchaseReturn': t.purchaseReturn,
      'StockIssue': t.stockIssue,
      'StockCount': t.stockCount,
      'Opening': t.openingBalance,
    };
    return typeMap[type] || type;
  };

  const getMovementTypeColor = (type: string): string => {
    const colorMap: { [key: string]: string } = {
      'PurchaseInvoice': theme.palette.success.main,
      'PurchaseReturn': theme.palette.error.main,
      'StockIssue': theme.palette.warning.main,
      'WarehouseTransfer': theme.palette.info.main,
      'BranchTransfer': theme.palette.info.main,
      'StockCount': theme.palette.secondary.main,
      'Adjustment': theme.palette.warning.main,
      'Opening': theme.palette.primary.main,
    };
    return colorMap[type] || theme.palette.grey[500];
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    let fromDate = '';
    let toDate = today.toISOString().split('T')[0];

    switch(preset) {
      case 'today':
        fromDate = toDate;
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      case 'last7Days':
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        fromDate = last7.toISOString().split('T')[0];
        break;
      case 'last30Days':
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 30);
        fromDate = last30.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setDateFromFilter(fromDate);
    setDateToFilter(toDate);
  };

  const clearAllFilters = () => {
    setItemFilter('');
    setWarehouseFilter('');
    setBranchFilter('');
    setMovementTypeFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setSearchQuery('');
    setDatePreset('');
    setPage(0);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (itemFilter) count++;
    if (warehouseFilter) count++;
    if (branchFilter) count++;
    if (movementTypeFilter) count++;
    if (dateFromFilter || dateToFilter) count++;
    if (searchQuery) count++;
    return count;
  };

  const getFilteredData = () => {
    let filtered = [...data];
    
    // Apply search filter only (main filtering is done by backend)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.itemName.toLowerCase().includes(query) ||
        m.itemNameAr?.toLowerCase().includes(query) ||
        m.referenceNumber.toLowerCase().includes(query) ||
        (m.notes && m.notes.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  const getDensitySize = () => {
    switch(tableDensity) {
      case 'compact': return 'small';
      case 'comfortable': return 'medium';
      default: return 'small';
    }
  };

  const getDensityPadding = () => {
    switch(tableDensity) {
      case 'compact': return 0.5;
      case 'comfortable': return 2;
      default: return 1;
    }
  };
  const getBalanceColor = (balance: number | undefined): string => {
    if (!balance) return theme.palette.text.secondary;
    if (balance < 0) return theme.palette.error.main;
    if (balance === 0) return theme.palette.warning.main;
    if (balance < 10) return theme.palette.warning.main;
    return theme.palette.success.main;
  };





  // Get subtotals for each item group
  const getItemSubtotals = (data: StockMovement[]) => {
    const subtotals = new Map<string, {
      itemName: string;
      itemNameAr: string;
      totalIn: number;
      totalOut: number;
      finalBalance: number;
      totalValue: number;
      movementCount: number;
    }>();

    data.filter((m: StockMovement) => !m.isOpeningRow).forEach((mov: StockMovement) => {
      const key = `${mov.itemId}_${mov.warehouseId || 0}`;
      if (!subtotals.has(key)) {
        subtotals.set(key, {
          itemName: mov.itemName,
          itemNameAr: mov.itemNameAr,
          totalIn: 0,
          totalOut: 0,
          finalBalance: mov.runningBalance || 0,
          totalValue: 0,
          movementCount: 0,
        });
      }
      
      const subtotal = subtotals.get(key)!;
      subtotal.totalIn += mov.quantityIn;
      subtotal.totalOut += mov.quantityOut;
      subtotal.finalBalance = mov.runningBalance || 0;
      subtotal.totalValue += mov.totalValue;
      subtotal.movementCount += 1;
    });

    return subtotals;
  };
  // Summary calculations
  const getSummaryByItem = () => {
    const filteredData = getFilteredData();
    const summary = new Map();
    
    filteredData.forEach(mov => {
      const key = `${mov.itemId}`;
      if (!summary.has(key)) {
        summary.set(key, {
          itemId: mov.itemId,
          itemCode: mov.itemCode,
          itemName: mov.itemName,
          itemNameAr: mov.itemNameAr,
          totalIn: 0,
          totalOut: 0,
          balance: 0,
          totalValue: 0,
          movementCount: 0,
          avgQuantity: 0,
        });
      }
      
      const item = summary.get(key);
      item.totalIn += mov.quantityIn;
      item.totalOut += mov.quantityOut;
      item.balance += (mov.quantityIn - mov.quantityOut);
      item.totalValue += mov.totalValue;
      item.movementCount += 1;
    });
    
    // Calculate average
    summary.forEach(item => {
      item.avgQuantity = item.movementCount > 0 ? (item.totalIn + item.totalOut) / item.movementCount : 0;
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getSummaryByWarehouse = () => {
    const filteredData = getFilteredData();
    const summary = new Map();
    
    filteredData.forEach(mov => {
      if (!mov.warehouseId) return;
      
      const key = `${mov.warehouseId}`;
      if (!summary.has(key)) {
        summary.set(key, {
          warehouseId: mov.warehouseId,
          warehouseCode: mov.warehouseCode,
          warehouseName: mov.warehouseName,
          warehouseNameAr: mov.warehouseNameAr,
          totalIn: 0,
          totalOut: 0,
          balance: 0,
          totalValue: 0,
          movementCount: 0,
        });
      }
      
      const warehouse = summary.get(key);
      warehouse.totalIn += mov.quantityIn;
      warehouse.totalOut += mov.quantityOut;
      warehouse.balance += (mov.quantityIn - mov.quantityOut);
      warehouse.totalValue += mov.totalValue;
      warehouse.movementCount += 1;
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getSummaryByType = () => {
    const filteredData = getFilteredData();
    const summary = new Map();
    
    filteredData.forEach(mov => {
      const key = mov.movementType;
      if (!summary.has(key)) {
        summary.set(key, {
          movementType: key,
          typeName: getMovementTypeLabel(key),
          totalIn: 0,
          totalOut: 0,
          balance: 0,
          totalValue: 0,
          movementCount: 0,
          avgValue: 0,
        });
      }
      
      const type = summary.get(key);
      type.totalIn += mov.quantityIn;
      type.totalOut += mov.quantityOut;
      type.balance += (mov.quantityIn - mov.quantityOut);
      type.totalValue += mov.totalValue;
      type.movementCount += 1;
    });
    
    // Calculate average
    summary.forEach(type => {
      type.avgValue = type.totalValue / type.movementCount;
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const isHighValueMovement = (movement: StockMovement) => {
    return movement.totalValue > 10000; // More than 10,000 KWD
  };
  const groupDataByType = () => {
    const grouped: { [key: string]: StockMovement[] } = {};
    const filteredData = getFilteredData();
    filteredData.forEach(movement => {
      const type = getMovementTypeLabel(movement.movementType);
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(movement);
    });
    return grouped;
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'حركات المخزون' : 'Stock Movements');

      // Add Logo
      if (companyInfo?.logoBase64) {
        try {
          const base64Data = companyInfo.logoBase64.split(',')[1];
          const imageId = workbook.addImage({
            base64: base64Data,
            extension: 'png',
          });
          worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Header
      worksheet.mergeCells('C1:I1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('C2:I2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 10 };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:I3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${t.kwd} | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9 };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.getRow(4).height = 10;

      // Report Title
      worksheet.mergeCells('A5:M5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      // Active Tab Name (below title)
      const tabNames = [t.detailedView, t.summaryByItem, t.summaryByWarehouse, t.summaryByType];
      worksheet.mergeCells('A6:M6');
      const tabCell = worksheet.getCell('A6');
      tabCell.value = `📊 ${tabNames[currentTab]}`;
      tabCell.font = { size: 12, bold: true, color: { argb: 'FF1565C0' } };
      tabCell.alignment = { horizontal: 'center', vertical: 'middle' };
      tabCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF5F5F5' }
      };

      // Date Badge
      worksheet.mergeCells('N5:O6');
      const dateCell = worksheet.getCell('N5');
      dateCell.value = new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
      dateCell.font = { size: 10, bold: true, color: { argb: 'FF1565C0' } };
      dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
      dateCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE3F2FD' }
      };

      // Filter Information (if any filters applied)
      let filterInfo = [];
      if (itemFilter && items.find(i => i.itemId === itemFilter)) {
        const item = items.find(i => i.itemId === itemFilter);
        filterInfo.push(`${t.item}: ${isRTL ? item?.itemNameAr : item?.itemName}`);
      }
      if (warehouseFilter && warehouses.find(w => w.warehouseId === warehouseFilter)) {
        const warehouse = warehouses.find(w => w.warehouseId === warehouseFilter);
        filterInfo.push(`${t.warehouse}: ${isRTL ? warehouse?.warehouseNameAr : warehouse?.warehouseName}`);
      }
      if (movementTypeFilter) {
        filterInfo.push(`${t.movementTypeLabel}: ${getMovementTypeLabel(movementTypeFilter)}`);
      }
      if (dateFromFilter && dateToFilter) {
        filterInfo.push(`${t.dateFrom}: ${formatDate(dateFromFilter)} - ${t.dateTo}: ${formatDate(dateToFilter)}`);
      }
      
      if (filterInfo.length > 0) {
        worksheet.mergeCells('A7:M7');
        const filterCell = worksheet.getCell('A7');
        filterCell.value = `🔍 ${t.filters}: ${filterInfo.join(' | ')}`;
        filterCell.font = { size: 10, italic: true, color: { argb: 'FF666666' } };
        filterCell.alignment = { horizontal: isRTL ? 'right' : 'left', vertical: 'middle' };
        filterCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFAFAFA' }
        };
        worksheet.getRow(8).height = 5;
      } else {
        worksheet.getRow(7).height = 5;
      }

      worksheet.getRow(9).height = 10;

      // Table Headers
      const headerRow = worksheet.getRow(10);
      const headers = [
        t.movementDate,
        t.referenceNumber,
        t.itemCode,
        t.itemName,
        t.unitName,
        t.warehouseName,
        t.branchName,
        t.movementTypeLabel,
        t.quantityIn,
        t.quantityOut,
        t.runningBalance,
        t.unitCost,
        t.totalValue,
      ];

      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1565C0' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF1565C0' } },
          bottom: { style: 'thin', color: { argb: 'FF1565C0' } },
          left: { style: 'thin', color: { argb: 'FF1565C0' } },
          right: { style: 'thin', color: { argb: 'FF1565C0' } }
        };
      });

      let currentRow = 11;

      // Get data based on current tab
      const dataToExport = getFilteredData();
      
      // Group by Movement Type
      const grouped = groupDataByType();
      Object.keys(grouped).forEach((typeName) => {
        const groupData = grouped[typeName];

        // Type Header
        worksheet.mergeCells(`A${currentRow}:M${currentRow}`);
        const typeHeaderCell = worksheet.getCell(`A${currentRow}`);
        typeHeaderCell.value = `📋 ${typeName}`;
        typeHeaderCell.font = { bold: true, size: 11, color: { argb: 'FF1565C0' } };
        typeHeaderCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF5F5F5' }
        };
        typeHeaderCell.alignment = { horizontal: isRTL ? 'right' : 'left', vertical: 'middle' };
        typeHeaderCell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
        currentRow++;

        let groupIn = 0;
        let groupOut = 0;

        groupData.forEach((movement, index) => {
          const row = worksheet.getRow(currentRow);
          const rowData = [
            formatDate(movement.movementDate),
            movement.referenceNumber,
            movement.itemCode,
            isRTL ? movement.itemNameAr : movement.itemName,
            isRTL ? movement.unitNameAr : movement.unitName,
            isRTL ? (movement.warehouseNameAr || movement.warehouseName) : movement.warehouseName,
            movement.branchName ? (isRTL ? movement.branchNameAr : movement.branchName) : '-',
            getMovementTypeLabel(movement.movementType),
            movement.quantityIn,
            movement.quantityOut,
            movement.runningBalance || 0,
            movement.unitCost,
            movement.totalValue,
          ];

          rowData.forEach((value, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            cell.value = value;
            cell.alignment = { 
              horizontal: colIndex >= 8 && colIndex <= 12 ? 'right' : 'left', 
              vertical: 'middle' 
            };
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA' }
            };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            };

            if (colIndex >= 8 && colIndex <= 12) {
              cell.numFmt = '#,##0.000';
            }
          });

          groupIn += movement.quantityIn;
          groupOut += movement.quantityOut;
          currentRow++;
        });

        // Type Subtotal
        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        const subtotalLabelCell = worksheet.getCell(`A${currentRow}`);
        subtotalLabelCell.value = `${t.typeTotal}: ${typeName}`;
        subtotalLabelCell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        subtotalLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
        subtotalLabelCell.fill = {
          type: 'gradient',
          gradient: 'angle',
          degree: 0,
          stops: [
            { position: 0, color: { argb: 'FF42A5F5' } },
            { position: 1, color: { argb: 'FF1976D2' } }
          ]
        };

        for (let col = 9; col <= 13; col++) {
          const cell = worksheet.getCell(currentRow, col);
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
          cell.fill = {
            type: 'gradient',
            gradient: 'angle',
            degree: 0,
            stops: [
              { position: 0, color: { argb: 'FF42A5F5' } },
              { position: 1, color: { argb: 'FF1976D2' } }
            ]
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          };

          if (col === 9) cell.value = groupIn;
          if (col === 10) cell.value = groupOut;
          
          if (col >= 9 && col <= 13) {
            cell.numFmt = '#,##0.000';
          }
        }

        currentRow++;
      });

      // Grand Total
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      const grandTotalLabelCell = worksheet.getCell(`A${currentRow}`);
      grandTotalLabelCell.value = `🏆 ${t.grandTotal}`;
      grandTotalLabelCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      grandTotalLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
      grandTotalLabelCell.fill = {
        type: 'gradient',
        gradient: 'angle',
        degree: 0,
        stops: [
          { position: 0, color: { argb: 'FF0D47A1' } },
          { position: 1, color: { argb: 'FF1565C0' } }
        ]
      };

      for (let col = 9; col <= 13; col++) {
        const cell = worksheet.getCell(currentRow, col);
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
          type: 'gradient',
          gradient: 'angle',
          degree: 0,
          stops: [
            { position: 0, color: { argb: 'FF0D47A1' } },
            { position: 1, color: { argb: 'FF1565C0' } }
          ]
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'medium' },
          bottom: { style: 'medium' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };

        if (col === 9) cell.value = stats.totalIn;
        if (col === 10) cell.value = stats.totalOut;
        
        if (col >= 9 && col <= 13) {
          cell.numFmt = '#,##0.000';
        }
      }

      // Column widths
      worksheet.columns = [
        { width: 14 },
        { width: 16 },
        { width: 14 },
        { width: 25 },
        { width: 10 },
        { width: 20 },
        { width: 18 },
        { width: 18 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `StockMovements_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(companyInfo?.companyName || 'Company Name', pageWidth / 2, 15, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, pageWidth / 2, 27, { align: 'center' });

      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Stock Movements Report', pageWidth / 2, 38.5, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(21, 101, 192);
      doc.text(new Date().toLocaleDateString('en-GB'), pageWidth - 15, 38.5, { align: 'right' });

      const tableHeaders = [
        'Date',
        'Doc #',
        'Item',
        'Warehouse',
        'Type',
        'In',
        'Out',
        'Balance',
      ];

      const tableData: any[] = [];
      const grouped = groupDataByType();

      Object.keys(grouped).forEach((typeName) => {
        const groupData = grouped[typeName];

        tableData.push([{
          content: typeName,
          colSpan: 8,
          styles: {
            fillColor: [25, 118, 210],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10,
            halign: 'left'
          }
        }]);

        let groupIn = 0;
        let groupOut = 0;

        groupData.forEach((movement) => {
          tableData.push([
            formatDate(movement.movementDate),
            movement.referenceNumber,
            (isRTL ? movement.itemNameAr : movement.itemName).substring(0, 20),
            (isRTL ? (movement.warehouseNameAr || movement.warehouseName || 'N/A') : (movement.warehouseName || 'N/A')).substring(0, 15),
            getMovementTypeLabel(movement.movementType).substring(0, 12),
            formatNumber(movement.quantityIn),
            formatNumber(movement.quantityOut),
            formatNumber(movement.runningBalance),
          ]);

          groupIn += movement.quantityIn;
          groupOut += movement.quantityOut;
        });

        tableData.push([
          {
            content: `Subtotal: ${typeName.substring(0, 15)}`,
            colSpan: 5,
            styles: {
              fillColor: [66, 165, 245],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              halign: 'center'
            }
          },
          {
            content: formatNumber(groupIn),
            styles: { fillColor: [66, 165, 245], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' }
          },
          {
            content: formatNumber(groupOut),
            styles: { fillColor: [66, 165, 245], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' }
          },
          {
            content: '',
            styles: { fillColor: [66, 165, 245] }
          }
        ]);
      });

      tableData.push([
        {
          content: 'GRAND TOTAL',
          colSpan: 5,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: formatNumber(stats.totalIn),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: formatNumber(stats.totalOut),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 251]
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 25 },
          2: { cellWidth: 45 },
          3: { cellWidth: 35 },
          4: { cellWidth: 30 },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 22, halign: 'right' },
          7: { cellWidth: 22, halign: 'right' },
        },
        margin: { left: 10, right: 10 },
      });

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.setFont('helvetica', 'italic');
        doc.text(
          `Generated on ${new Date().toLocaleString('en-GB')} | Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      doc.save(`StockMovements_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    // Store current tab to restore after print
    const currentTabValue = currentTab;
    
    // Set print-specific styles based on active tab
    const printStyleElement = document.getElementById('print-styles');
    if (printStyleElement) {
      printStyleElement.innerHTML = `
        @media print {
          .tab-content-${currentTabValue} {
            display: block !important;
          }
          .tab-content:not(.tab-content-${currentTabValue}) {
            display: none !important;
          }
        }
      `;
    }
    
    window.print();
  };

  const getPaginatedData = () => {
    const filteredData = getFilteredData();
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Print Section */}
      <Box className="print-section">
        <ReportHeader
          reportTitle={t.title}
          reportTitleAr={t.titleAr}
          reportDate={new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
        />
        
        {/* Active Tab & Filter Info */}
        <Box sx={{ textAlign: 'center', mb: 2, mt: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
            📊 {[t.detailedView, t.summaryByItem, t.summaryByWarehouse, t.summaryByType][currentTab]}
          </Typography>
          {(itemFilter || warehouseFilter || movementTypeFilter || dateFromFilter) && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              🔍 {t.filters}: {[
                itemFilter && items.find(i => i.itemId === itemFilter) && `${t.item}: ${isRTL ? items.find(i => i.itemId === itemFilter)?.itemNameAr : items.find(i => i.itemId === itemFilter)?.itemName}`,
                warehouseFilter && warehouses.find(w => w.warehouseId === warehouseFilter) && `${t.warehouse}: ${isRTL ? warehouses.find(w => w.warehouseId === warehouseFilter)?.warehouseNameAr : warehouses.find(w => w.warehouseId === warehouseFilter)?.warehouseName}`,
                movementTypeFilter && `${t.movementTypeLabel}: ${getMovementTypeLabel(movementTypeFilter)}`,
                dateFromFilter && dateToFilter && `${formatDate(dateFromFilter)} - ${formatDate(dateToFilter)}`
              ].filter(Boolean).join(' | ')}
            </Typography>
          )}
        </Box>
        
        <TableContainer sx={{ mt: 3 }}>
          <Table size="small" sx={{ 
            fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
            '& th, & td': { 
              border: '1px solid #1565C0',
              p: 1.5,
              fontSize: '12px',
              fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
              fontWeight: 500,
              color: '#000'
            },
            '& th': {
              fontWeight: 700
            }
          }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1565C0' }}>
                {visibleColumns.movementDate && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.movementDate}</TableCell>}
                {visibleColumns.referenceNumber && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.referenceNumber}</TableCell>}
                {visibleColumns.itemName && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.itemName}</TableCell>}
                {visibleColumns.movementType && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.movementTypeLabel}</TableCell>}
                {visibleColumns.quantityIn && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.quantityIn}</TableCell>}
                {visibleColumns.quantityOut && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.quantityOut}</TableCell>}
                {visibleColumns.runningBalance && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{t.runningBalance}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredData().map((movement, index) => (
                <TableRow key={`${movement.movementId}_${index}`} sx={{ bgcolor: index % 2 === 0 ? 'white' : '#f5f5f5' }}>
                  {visibleColumns.movementDate && <TableCell>{formatDate(movement.movementDate)}</TableCell>}
                  {visibleColumns.referenceNumber && <TableCell>{movement.referenceNumber}</TableCell>}
                  {visibleColumns.itemName && <TableCell>{isRTL ? movement.itemNameAr : movement.itemName}</TableCell>}
                  {visibleColumns.movementType && <TableCell>{getMovementTypeLabel(movement.movementType)}</TableCell>}
                  {visibleColumns.quantityIn && <TableCell align="right">{formatNumber(movement.quantityIn)}</TableCell>}
                  {visibleColumns.quantityOut && <TableCell align="right">{formatNumber(movement.quantityOut)}</TableCell>}
                  {visibleColumns.runningBalance && (
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={getBalanceColor(movement.runningBalance)}>
                          {formatNumber(movement.runningBalance)}
                        </Typography>
                      </TableCell>
                    )}
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: '#0D47A1' }}>
                <TableCell colSpan={visibleColumns.movementType ? 4 : 3} sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{t.grandTotal}</TableCell>
                {visibleColumns.quantityIn && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{formatNumber(stats.totalIn)}</TableCell>}
                {visibleColumns.quantityOut && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{formatNumber(stats.totalOut)}</TableCell>}
                {visibleColumns.runningBalance && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '13px' }}>{formatNumber(stats.netMovement)}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Screen Section */}
      <Box className="no-print" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 2, display: 'flex' }}>
            <MovementIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {isRTL ? t.titleAr : t.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isRTL ? 'تقرير شامل لحركات المخزون الواردة والصادرة' : 'Comprehensive report of all stock in and out movements'}
            </Typography>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Stats Cards */}
      <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{stats.totalMovements}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalMovements}</Typography>
              </Box>
              <StockIcon sx={{ fontSize: 50, opacity: 0.3, color: 'primary.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="success.main">{formatNumber(stats.totalIn, 0)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalIn}</Typography>
              </Box>
              <InIcon sx={{ fontSize: 50, opacity: 0.3, color: 'success.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="error.main">{formatNumber(stats.totalOut, 0)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalOut}</Typography>
              </Box>
              <OutIcon sx={{ fontSize: 50, opacity: 0.3, color: 'error.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold" color={stats.netMovement >= 0 ? 'success.main' : 'error.main'}>
                  {formatNumber(stats.netMovement, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">{t.netMovement}</Typography>
              </Box>
              <DateIcon sx={{ fontSize: 50, opacity: 0.3, color: 'info.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {formatNumber(stats.totalValue, 3)} {t.kwd}
                </Typography>
                <Typography variant="body2" color="text.secondary">{t.totalValue}</Typography>
              </Box>
              <StockIcon sx={{ fontSize: 50, opacity: 0.3, color: 'primary.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="secondary.main">
                  {formatNumber(stats.averageCost, 3)} {t.kwd}
                </Typography>
                <Typography variant="body2" color="text.secondary">{t.averageCost}</Typography>
              </Box>
              <MovementIcon sx={{ fontSize: 50, opacity: 0.3, color: 'secondary.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Search & Quick Actions */}
      <Paper className="no-print" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ flex: '1 1 300px', minWidth: 250 }}
          />
          
          <Button
            size="small"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'contained' : 'outlined'}
          >
            {t.filters}
            {getActiveFiltersCount() > 0 && (
              <Chip
                label={getActiveFiltersCount()}
                size="small"
                color="error"
                sx={{ ml: 1, height: 20, minWidth: 20 }}
              />
            )}
          </Button>

          {getActiveFiltersCount() > 0 && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              color="error"
              variant="outlined"
            >
              {t.clearFilters}
            </Button>
          )}

          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            <Button
              size="small"
              variant={tableDensity === 'compact' ? 'contained' : 'outlined'}
              onClick={() => setTableDensity('compact')}
              sx={{ minWidth: 40, px: 1 }}
            >
              <CompactIcon fontSize="small" />
            </Button>
            <Button
              size="small"
              variant={tableDensity === 'standard' ? 'contained' : 'outlined'}
              onClick={() => setTableDensity('standard')}
              sx={{ minWidth: 40, px: 1 }}
            >
              <StandardIcon fontSize="small" />
            </Button>
            <Button
              size="small"
              variant={tableDensity === 'comfortable' ? 'contained' : 'outlined'}
              onClick={() => setTableDensity('comfortable')}
              sx={{ minWidth: 40, px: 1 }}
            >
              <ComfortableIcon fontSize="small" />
            </Button>
          </Box>
        </Box>

        {/* Date Presets */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
            {t.dateFrom}:
          </Typography>
          <Chip
            label={t.today}
            size="small"
            onClick={() => handleDatePreset('today')}
            color={datePreset === 'today' ? 'primary' : 'default'}
            variant={datePreset === 'today' ? 'filled' : 'outlined'}
          />
          <Chip
            label={t.thisWeek}
            size="small"
            onClick={() => handleDatePreset('thisWeek')}
            color={datePreset === 'thisWeek' ? 'primary' : 'default'}
            variant={datePreset === 'thisWeek' ? 'filled' : 'outlined'}
          />
          <Chip
            label={t.thisMonth}
            size="small"
            onClick={() => handleDatePreset('thisMonth')}
            color={datePreset === 'thisMonth' ? 'primary' : 'default'}
            variant={datePreset === 'thisMonth' ? 'filled' : 'outlined'}
          />
          <Chip
            label={t.last7Days}
            size="small"
            onClick={() => handleDatePreset('last7Days')}
            color={datePreset === 'last7Days' ? 'primary' : 'default'}
            variant={datePreset === 'last7Days' ? 'filled' : 'outlined'}
          />
          <Chip
            label={t.last30Days}
            size="small"
            onClick={() => handleDatePreset('last30Days')}
            color={datePreset === 'last30Days' ? 'primary' : 'default'}
            variant={datePreset === 'last30Days' ? 'filled' : 'outlined'}
          />
          <Chip
            label={t.thisYear}
            size="small"
            onClick={() => handleDatePreset('thisYear')}
            color={datePreset === 'thisYear' ? 'primary' : 'default'}
            variant={datePreset === 'thisYear' ? 'filled' : 'outlined'}
          />
        </Box>
      </Paper>

      {/* Advanced Filters */}
      {showFilters && (
        <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allItems }, ...items.map(item => ({ id: item.itemId as number | '', label: isRTL ? item.itemNameAr || item.itemName : item.itemName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allItems }, ...items.map(item => ({ id: item.itemId as number | '', label: isRTL ? item.itemNameAr || item.itemName : item.itemName }))].find(o => o.id === itemFilter) || { id: '' as number | '', label: t.allItems }}
            onChange={(_, v) => setItemFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.item} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 200 }}
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 200 }}
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 200 }}
          />

          <TextField
            select
            size="small"
            label={t.movementType}
            value={movementTypeFilter}
            onChange={(e) => setMovementTypeFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t.allTypes}</MenuItem>
            <MenuItem value="PurchaseInvoice">{t.purchaseInvoice}</MenuItem>
            <MenuItem value="PurchaseReturn">{t.purchaseReturn}</MenuItem>
            <MenuItem value="StockIssue">{t.stockIssue}</MenuItem>
            <MenuItem value="StockCount">{t.stockCount}</MenuItem>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateFrom}
              value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateTo}
              value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
          </LocalizationProvider>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData} variant="outlined">
            {t.refresh}
          </Button>
          <Button startIcon={<ExcelIcon />} onClick={exportToExcel} variant="contained" color="success">
            {t.exportExcel}
          </Button>
          <Button startIcon={<PdfIcon />} onClick={exportToPdf} variant="contained" color="error">
            {t.exportPdf}
          </Button>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="contained">
            {t.print}
          </Button>
          <Button startIcon={<ColumnsIcon />} onClick={(e) => setColumnsAnchor(e.currentTarget)} variant="outlined">
            {t.columns}
          </Button>
        </Box>
        </Paper>
      )}

      {/* Tab Content - Summary by Item */}
      {currentTab === 1 && (
        <TableContainer component={Paper} className="no-print tab-content-1" sx={{ mb: 2 }}>
          <Table size={getDensitySize() as any}>
            <TableHead>
              <TableRow sx={{ 
                bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemCode}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalMovementsCount}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalIn}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalOut}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.currentBalance}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgQuantity}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSummaryByItem().map((item, index) => (
                <TableRow 
                  key={item.itemId}
                  hover
                  sx={{ 
                    bgcolor: index % 2 === 0 ? 'background.paper' : alpha(theme.palette.primary.main, 0.02),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{item.itemCode}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {isRTL ? item.itemNameAr : item.itemName}
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={item.movementCount} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight="bold">
                      {formatNumber(item.totalIn, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="error.main" fontWeight="bold">
                      {formatNumber(item.totalOut, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      fontWeight="bold" 
                      color={getBalanceColor(item.balance)}
                      sx={{ fontSize: '1.05rem' }}
                    >
                      {formatNumber(item.balance, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(item.avgQuantity, 2)}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary.main">
                      {formatNumber(item.totalValue, 3)} {t.kwd}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {getSummaryByItem().length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}



      {/* Tab Content - Summary by Warehouse */}
      {currentTab === 2 && (
        <TableContainer component={Paper} className="no-print tab-content-2" sx={{ mb: 2 }}>
          <Table size={getDensitySize() as any}>
            <TableHead>
              <TableRow sx={{ 
                bgcolor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalMovementsCount}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalIn}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalOut}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.currentBalance}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSummaryByWarehouse().map((warehouse, index) => (
                <TableRow 
                  key={warehouse.warehouseId}
                  hover
                  sx={{ 
                    bgcolor: index % 2 === 0 ? 'background.paper' : alpha(theme.palette.secondary.main, 0.02),
                    '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) }
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StockIcon color="action" fontSize="small" />
                      {isRTL ? warehouse.warehouseNameAr : warehouse.warehouseName}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={warehouse.movementCount} size="small" color="secondary" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight="bold">
                      {formatNumber(warehouse.totalIn, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="error.main" fontWeight="bold">
                      {formatNumber(warehouse.totalOut, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      fontWeight="bold" 
                      color={getBalanceColor(warehouse.balance)}
                      sx={{ fontSize: '1.05rem' }}
                    >
                      {formatNumber(warehouse.balance, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="secondary.main">
                      {formatNumber(warehouse.totalValue, 3)} {t.kwd}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {getSummaryByWarehouse().length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab Content - Summary by Type */}
      {currentTab === 3 && (
        <TableContainer component={Paper} className="no-print tab-content-3" sx={{ mb: 2 }}>
          <Table size={getDensitySize() as any}>
            <TableHead>
              <TableRow sx={{ 
                bgcolor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.movementTypeLabel}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalMovementsCount}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalIn}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalOut}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.netMovement}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.averageCost}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSummaryByType().map((type, index) => (
                <TableRow 
                  key={type.movementType}
                  hover
                  sx={{ 
                    bgcolor: index % 2 === 0 ? 'background.paper' : alpha(theme.palette.info.main, 0.02),
                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.08) }
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={type.typeName} 
                      size="small"
                      sx={{ 
                        bgcolor: getMovementTypeColor(type.movementType),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={type.movementCount} size="small" color="info" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight="bold">
                      {formatNumber(type.totalIn, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="error.main" fontWeight="bold">
                      {formatNumber(type.totalOut, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      fontWeight="bold" 
                      color={type.balance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ fontSize: '1.05rem' }}
                    >
                      {formatNumber(type.balance, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="info.main">
                      {formatNumber(type.totalValue, 3)} {t.kwd}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(type.avgValue, 3)} {t.kwd}
                  </TableCell>
                </TableRow>
              ))}
              {getSummaryByType().length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Columns Popover */}
      <Popover
        open={Boolean(columnsAnchor)}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t.columns}
          </Typography>
          <FormGroup>
            {Object.keys(visibleColumns).map((col) => (
              <FormControlLabel
                key={col}
                control={
                  <Checkbox
                    checked={visibleColumns[col as keyof typeof visibleColumns]}
                    onChange={() => handleColumnToggle(col as keyof typeof visibleColumns)}
                  />
                }
                label={t[col as keyof typeof t]}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>


      {/* View Tabs */}
      <Paper className="no-print" sx={{ mb: 2 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
            }
          }}
        >
          <Tab 
            icon={<TableChartIcon />} 
            iconPosition="start" 
            label={t.detailedView}
          />
          <Tab 
            icon={<AnalyticsIcon />} 
            iconPosition="start" 
            label={t.summaryByItem}
          />
          <Tab 
            icon={<StockIcon />} 
            iconPosition="start" 
            label={t.summaryByWarehouse}
          />
          <Tab 
            icon={<MovementIcon />} 
            iconPosition="start" 
            label={t.summaryByType}
          />
        </Tabs>
      </Paper>

      {/* Tab Content - Detailed View */}
      {currentTab === 0 && (
        <>
        <TableContainer component={Paper} className="no-print tab-content-0" sx={{ mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size={getDensitySize() as any}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, isDark ? 0.3 : 0.1) }}>
                {visibleColumns.movementDate && <TableCell><strong>{t.movementDate}</strong></TableCell>}
                {visibleColumns.referenceNumber && <TableCell><strong>{t.referenceNumber}</strong></TableCell>}
                {visibleColumns.itemCode && <TableCell><strong>{t.itemCode}</strong></TableCell>}
                {visibleColumns.itemName && <TableCell><strong>{t.itemName}</strong></TableCell>}
                {visibleColumns.unitName && <TableCell><strong>{t.unitName}</strong></TableCell>}
                {visibleColumns.warehouseName && <TableCell><strong>{t.warehouseName}</strong></TableCell>}
                {visibleColumns.branchName && <TableCell><strong>{t.branchName}</strong></TableCell>}
                {visibleColumns.movementType && <TableCell><strong>{t.movementTypeLabel}</strong></TableCell>}
                {visibleColumns.quantityIn && <TableCell align="right"><strong>{t.quantityIn}</strong></TableCell>}
                {visibleColumns.quantityOut && <TableCell align="right"><strong>{t.quantityOut}</strong></TableCell>}
                {visibleColumns.runningBalance && <TableCell align="right"><strong>{t.runningBalance}</strong></TableCell>}
                {visibleColumns.unitCost && <TableCell align="right"><strong>{t.unitCost}</strong></TableCell>}
                {visibleColumns.totalValue && <TableCell align="right"><strong>{t.totalValue}</strong></TableCell>}
                {visibleColumns.movementType && <TableCell><strong>{t.notes}</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredData().length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getPaginatedData().map((movement, index) => (
                  <TableRow 
                  key={`${movement.movementId}_${index}`} 
                  hover
                  sx={{
                    bgcolor: movement.isOpeningRow
                      ? alpha(theme.palette.info.main, 0.15)
                      : isHighValueMovement(movement) 
                      ? alpha(theme.palette.warning.main, 0.05)
                      : 'transparent',
                    borderLeft: movement.isOpeningRow
                      ? `4px solid ${theme.palette.info.main}`
                      : isHighValueMovement(movement)
                      ? `3px solid ${theme.palette.warning.main}`
                      : 'none',
                    fontWeight: movement.isOpeningRow ? 'bold' : 'normal',
                  }}
                >
                    {visibleColumns.movementDate && <TableCell>{formatDate(movement.movementDate)}</TableCell>}
                    {visibleColumns.referenceNumber && <TableCell>{movement.referenceNumber}</TableCell>}
                    {visibleColumns.itemCode && <TableCell>{movement.itemCode}</TableCell>}
                    {visibleColumns.itemName && <TableCell>{isRTL ? movement.itemNameAr : movement.itemName}</TableCell>}
                    {visibleColumns.unitName && <TableCell>{isRTL ? movement.unitNameAr : movement.unitName}</TableCell>}
                    {visibleColumns.warehouseName && <TableCell>{isRTL ? movement.warehouseNameAr : movement.warehouseName}</TableCell>}
                    {visibleColumns.branchName && <TableCell>{movement.branchName ? (isRTL ? movement.branchNameAr : movement.branchName) : '-'}</TableCell>}
                    {visibleColumns.movementType && (
                      <TableCell>
                        <Chip
                          label={getMovementTypeLabel(movement.movementType)}
                          size="small"
                          sx={{
                            bgcolor: alpha(getMovementTypeColor(movement.movementType), 0.1),
                            color: getMovementTypeColor(movement.movementType),
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.quantityIn && (
                      <TableCell align="right">
                        <Typography color="success.main" fontWeight={movement.quantityIn > 0 ? 'bold' : 'normal'}>
                          {movement.quantityIn > 0 ? formatNumber(movement.quantityIn) : '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.quantityOut && (
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight={movement.quantityOut > 0 ? 'bold' : 'normal'}>
                          {movement.quantityOut > 0 ? formatNumber(movement.quantityOut) : '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.runningBalance && (
                      <TableCell align="right">
                        <Typography fontWeight="bold" color={getBalanceColor(movement.runningBalance)}>
                          {formatNumber(movement.runningBalance)}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.unitCost && <TableCell align="right">{fmtUnitPrice(movement.unitCost)}</TableCell>}
                    {visibleColumns.totalValue && <TableCell align="right">{formatNumber(movement.totalValue)}</TableCell>}
                    {visibleColumns.movementType && <TableCell>{movement.notes || '-'}</TableCell>}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        </TableContainer>
        <TablePagination
          component="div"
          count={getFilteredData().length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t.rowsPerPage}
        />
        </>
      )}

      <style>
        {`
          @media print {
            @page { 
              size: A4 landscape; 
              margin: 10mm;
            }
            body * {
              visibility: hidden;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            .print-section, .print-section * {
              visibility: visible;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            .print-section {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }
            .no-print { display: none !important; }
            table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: auto;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            th, td {
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
              font-size: 11pt !important;
              font-weight: 500 !important;
              color: #000 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            th {
              font-weight: 700 !important;
            }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
          @media screen {
            .print-section { display: none; }
          }
        `}
      </style>
    </Box>
  );
};

export default StockMovementsReport;